import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Megaphone, LogOut, User, Sparkles, Eye } from "lucide-react";
import { openPalette } from "@/components/layout/CommandPalette";
import { NotificationBell } from "@/components/layout/ShellExtras";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/common/UserAvatar";
import { BrandMark } from "@/components/common/BrandMark";
import { ROLE_LABELS } from "@/lib/constants";

const CampaignSelector = () => {
  const { campaign, setCampaign } = useAuth();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(campaign);
  const save = (value) => {
    setCampaign(value.trim());
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(campaign); }}>
      <PopoverTrigger asChild>
        <Button variant="secondary" className="max-w-[240px]" data-testid="campaign-selector-trigger">
          <Megaphone className="!h-4 !w-4 text-muted-foreground" aria-hidden="true" />
          <span className="truncate">
            <span className="hidden text-muted-foreground lg:inline">Campaign: </span>
            {campaign || "none"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-xl" data-testid="campaign-selector-panel">
        <form onSubmit={(e) => { e.preventDefault(); save(draft); }} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="campaign-input">Current campaign</Label>
            <Input id="campaign-input" value={draft} maxLength={120} onChange={(e) => setDraft(e.target.value)} placeholder="For example, Q3 debt advisory push" data-testid="campaign-input" />
            <p className="text-xs text-muted-foreground">Passed to every run in this session. Leave empty for none.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => save("")} data-testid="campaign-clear-button">Clear</Button>
            <Button type="submit" size="sm" data-testid="campaign-save-button">Set campaign</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};

const UserMenu = () => {
  const { user, realUser, logout, viewAs, setViewAs } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="rounded-full transition-quick hover:opacity-90" aria-label="Open your menu" data-testid="user-menu-trigger">
          <UserAvatar user={user} testid="user-menu-avatar" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 rounded-xl">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium" data-testid="user-menu-name">{user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="user-menu-role">{ROLE_LABELS[user.role]}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/profile")} data-testid="user-menu-profile"><User className="!h-4 !w-4" /> Profile</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/whats-new")} data-testid="user-menu-whats-new"><Sparkles className="!h-4 !w-4" /> What's new</DropdownMenuItem>
        {realUser.role === "owner" && (
          <>
            <DropdownMenuSeparator />
            {viewAs ? <DropdownMenuItem onSelect={() => setViewAs(null)} data-testid="user-menu-view-exit"><Eye className="!h-4 !w-4" /> Leave preview</DropdownMenuItem> : (
              <>
                <DropdownMenuItem onSelect={() => { setViewAs("member"); navigate("/"); }} data-testid="user-menu-view-member"><Eye className="!h-4 !w-4" /> View as Member</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setViewAs("viewer"); navigate("/"); }} data-testid="user-menu-view-viewer"><Eye className="!h-4 !w-4" /> View as Viewer</DropdownMenuItem>
              </>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={async () => { await logout(); navigate("/signin"); }} data-testid="user-menu-sign-out"><LogOut className="!h-4 !w-4" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Topbar = () => (
  <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur-md" data-testid="topbar">
    <div className="brand-strip h-[3px]" aria-hidden="true" />
    <div className="flex h-16 items-center gap-3 px-4 md:px-8">
      <Link to="/" className="md:hidden" aria-label="Home"><BrandMark /></Link>
      <button type="button" onClick={openPalette} className="relative hidden h-10 max-w-xl flex-1 items-center gap-2 rounded-xl border border-input bg-card px-3 text-left text-sm text-muted-foreground transition-quick hover:border-foreground/30 sm:flex" aria-label="Search the hub" data-testid="global-search-input">
        <Search className="!h-4 !w-4" aria-hidden="true" /> <span className="flex-1">Search agents, workflows and prompts</span>
        <kbd className="rounded-md border bg-muted px-1.5 py-0.5 font-mono text-xs">Ctrl K</kbd>
      </button>
      <div className="ml-auto flex items-center gap-2">
        <Button variant="ghost" size="icon" className="sm:hidden" onClick={openPalette} aria-label="Search" data-testid="mobile-search-button"><Search /></Button>
        <CampaignSelector />
        <NotificationBell />
        <UserMenu />
      </div>
    </div>
  </header>
);
